import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useDocuments } from "../hooks/useDocuments";
import { Select } from "@/shared/ui";
import { getCountries, type CountryConfig } from "@/shared/config";
import { useConfirm } from "@/hooks/useConfirm";

export const DocumentsModerationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [countries, setCountries] = useState<CountryConfig[]>([]);

  useEffect(() => {
    getCountries().then(setCountries);
  }, []);
  const { confirm, Modal: ConfirmModal } = useConfirm();
  const {
    documents,
    total,
    loading,
    error,
    filters,
    pagination,
    updateFilters,
    updatePagination,
    deleteDocument,
  } = useDocuments();

  const handleFilterChange = (key: string, value: any) => {
    updateFilters({ [key]: value === "all" ? undefined : value });
  };

  const handleDelete = async (documentId: string, title: string) => {
    const confirmed = await confirm({
      title: t("admin.documents.confirmDeleteTitle"),
      message: t("admin.documents.confirmDelete", { title }),
      confirmText: t("common.delete"),
      variant: "danger",
    });
    if (!confirmed) return;
    await deleteDocument(documentId);
  };

  const totalPages = Math.ceil(total / (pagination.pageSize || 50));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-juridico">
          {t("admin.documents.title")}
        </h1>
        <p className="text-juridico/60 mt-1">{t("admin.documents.subtitle")}</p>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-romance-200 p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Country Filter */}
          <Select
            value={filters.country || "all"}
            onChange={(e) => handleFilterChange("country", e.target.value)}
            options={[
              {
                value: "all",
                label: t("admin.documents.filters.allCountries"),
              },
              ...countries.map((c) => ({
                value: c.code,
                label: i18n.language === "es" ? c.nameEs : c.nameEn,
              })),
            ]}
          />

          {/* Status Filter */}
          <Select
            value={filters.status || "all"}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            options={[
              { value: "all", label: t("admin.documents.filters.allStatus") },
              { value: "draft", label: t("admin.documents.filters.draft") },
              { value: "final", label: t("admin.documents.filters.final") },
            ]}
          />
        </div>

        <div className="mt-4">
          <p className="text-sm font-light text-tiber-900">
            {t("admin.documents.showing", { count: documents.length, total })}
          </p>
        </div>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg border border-romance-200 overflow-hidden"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-juridico/60">{t("admin.documents.noResults")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-romance-200">
              <thead className="bg-romance-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.documents.table.title")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.documents.table.user")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.documents.table.type")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.documents.table.country")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.documents.table.status")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.documents.table.created")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-juridico uppercase tracking-wider">
                    {t("admin.documents.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-romance-200">
                {documents.map((doc) => (
                  <tr
                    key={doc.id}
                    className="hover:bg-romance-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-juridico max-w-xs truncate">
                        {doc.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-juridico">
                          {doc.user_name || t("admin.documents.notAvailable")}
                        </div>
                        <div className="text-sm text-juridico/60">
                          {doc.user_email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-juridico">
                      {doc.document_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-juridico">
                      {doc.country
                        ? (() => {
                            const c = countries.find(
                              (x) => x.code === doc.country
                            );
                            return c
                              ? i18n.language === "es"
                                ? c.nameEs
                                : c.nameEn
                              : doc.country;
                          })()
                        : t("admin.documents.notAvailable")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          doc.status === "final"
                            ? "bg-atlantis-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {t(`admin.documents.status.${doc.status}`)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-juridico/60">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleDelete(doc.id, doc.title)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title={t("admin.documents.actions.delete")}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-romance-200 flex items-center justify-between">
            <button
              onClick={() =>
                updatePagination({ page: (pagination.page || 1) - 1 })
              }
              disabled={pagination.page === 1}
              className="px-4 py-2 text-sm font-medium text-juridico bg-white border border-romance-200 rounded-lg hover:bg-romance-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("common.previous")}
            </button>
            <span className="text-sm text-juridico">
              {t("common.pageOfPages", {
                current: pagination.page,
                total: totalPages,
              })}
            </span>
            <button
              onClick={() =>
                updatePagination({ page: (pagination.page || 1) + 1 })
              }
              disabled={pagination.page === totalPages}
              className="px-4 py-2 text-sm font-medium text-juridico bg-white border border-romance-200 rounded-lg hover:bg-romance-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </motion.div>

      {ConfirmModal}
    </div>
  );
};
